/**
 * Freshness slice — docs/ARCHITECTURE.md §5.4.
 *
 * The one rule this file exists to enforce: **`lastOkAt` advances only on
 * `ok === true && stale === false`**. Everything the chip says follows from
 * that, which is what retires INVENTORY §12.3 (the prototype's `_chkAt` was
 * bumped by focus alone, so the chip was theater).
 */

import type { StateCreator } from 'zustand';

import type { ErrCode } from '../../lib/types.ts';
import type { QdStore } from '../types.ts';

export type FreshnessState = 'idle' | 'busy' | 'ok' | 'retrying';

export interface Freshness {
  /** the real `_chkAt` — advances ONLY on ok && !stale */
  lastOkAt: number | null;
  lastAttemptAt: number | null;
  state: FreshnessState;
  /** consecutive failures; drives the 1500·2ⁿ backoff */
  failures: number;
  lastError: ErrCode | null;
}

/** §5.4: `min(1500·2^failures, 96_000)`, cap 7 (the prototype's giFlush ladder). */
export const BACKOFF_BASE_MS = 1500;
export const BACKOFF_MAX_MS = 96_000;
export const BACKOFF_CAP = 7;
/** `retrying` with this many consecutive failures reads OFFLINE. */
export const OFFLINE_AFTER = 5;

export function backoffMs(failures: number): number {
  return Math.min(BACKOFF_BASE_MS * Math.pow(2, Math.min(failures, BACKOFF_CAP)), BACKOFF_MAX_MS);
}

export interface FreshnessSlice {
  freshness: Freshness;
  freshBusy: () => void;
  freshOk: () => void;
  freshFail: (code: ErrCode) => void;
}

export const initialFreshness: Freshness = {
  lastOkAt: null,
  lastAttemptAt: null,
  state: 'idle',
  failures: 0,
  lastError: null,
};

export const createFreshnessSlice: StateCreator<QdStore, [], [], FreshnessSlice> = (set) => ({
  freshness: { ...initialFreshness },

  freshBusy: () =>
    set((s) => ({ freshness: { ...s.freshness, state: 'busy', lastAttemptAt: Date.now() } })),

  freshOk: () =>
    set(() => ({
      freshness: {
        lastOkAt: Date.now(),
        lastAttemptAt: Date.now(),
        state: 'ok',
        failures: 0,
        lastError: null,
      },
    })),

  freshFail: (code) =>
    set((s) => ({
      freshness: {
        // NOT touched — see the module header.
        lastOkAt: s.freshness.lastOkAt,
        lastAttemptAt: Date.now(),
        state: 'retrying',
        failures: s.freshness.failures + 1,
        lastError: code,
      },
    })),
});
